/**
 * SharedChatPage — Iter 153.43 (Feb 9, 2026, Yash).
 *
 * Public read-only chat viewer reached via /share/chat/:token.
 * No auth required. Token is validated server-side; expired or revoked
 * tokens return 410.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Calendar, Phone, ShieldCheck } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SharedChatPage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    const fetchShare = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/chat-share/${token}`);
        if (!cancelled) setState({ loading: false, data, error: null });
      } catch (err) {
        if (cancelled) return;
        const status = err.response?.status;
        const detail = err.response?.data?.detail || "Could not load chat";
        let msg = detail;
        if (status === 410) msg = "This share link has expired or been revoked.";
        if (status === 404) msg = "This share link doesn't exist.";
        setState({ loading: false, data: null, error: msg });
      }
    };
    fetchShare();
    return () => { cancelled = true; };
  }, [token]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto rounded-full border-4 border-slate-200 border-t-[--accent] animate-spin"></div>
          <p className="mt-3 text-[13px] text-[--text-muted]">Loading shared chat…</p>
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="shared-chat-error">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-[--border] p-6 text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-red-50 flex items-center justify-center mb-3">
            <ShieldCheck size={26} className="text-red-500" />
          </div>
          <h1 className="text-[16px] font-bold text-[--text-primary] mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>Link unavailable</h1>
          <p className="text-[13px] text-[--text-muted]">{state.error}</p>
        </div>
      </div>
    );
  }

  const { lead, messages = [], expires_at, created_by_name } = state.data;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="shared-chat-page">
      <header className="bg-white border-b border-[--border] sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center gap-4">
          <div className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ background: "linear-gradient(135deg, #E11D48, #EA580C)" }}>
            {lead.name?.charAt(0)?.toUpperCase() || "?"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-[15px] text-[--text-primary] truncate" style={{ fontFamily: "Outfit, sans-serif" }}>{lead.name || "(no name)"}</div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-[--text-muted]">
              <span className="inline-flex items-center gap-1"><Phone size={11} /> +{lead.phone}</span>
              <span className="inline-flex items-center gap-1"><Calendar size={11} /> Expires {new Date(expires_at).toLocaleDateString()}</span>
              {created_by_name && <span>Shared by {created_by_name}</span>}
            </div>
          </div>
          <span className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200">
            <ShieldCheck size={11} /> Read-only
          </span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-6">
        {messages.length === 0 ? (
          <p className="text-center text-[--text-muted] text-[13px] py-12">No messages in this chat yet.</p>
        ) : (
          <div className="space-y-3" data-testid="shared-chat-messages">
            {messages.map((m, idx) => {
              // Iter 153.48 — Stable key per message. Was bare `idx`
              // which can mis-reconcile if the public viewer ever
              // streams new messages live. Prefer m.id → timestamp →
              // idx fallback.
              const key = m.id || (m.timestamp ? `${m.timestamp}-${idx}` : idx);
              const isOut = m.direction === "outgoing";
              const sender = m.sender_name || (m.is_ai_reply ? "AI" : isOut ? "Agent" : (lead.name || "Customer"));
              return (
                <div key={key} className={`flex ${isOut ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] sm:max-w-[70%] rounded-2xl px-4 py-2.5 shadow-sm ${isOut ? "bg-[#dcf8c6] rounded-br-md" : "bg-white border border-[--border] rounded-bl-md"}`}>
                    {!isOut && (
                      <div className="text-[10px] font-bold text-[--accent] mb-0.5">{sender}</div>
                    )}
                    {m.media_url && (
                      <a href={m.media_url} target="_blank" rel="noopener noreferrer" className="block text-[11px] text-blue-600 underline mb-1 break-all">
                        📎 {m.type || "media"}
                      </a>
                    )}
                    <p className="text-[13px] text-[--text-primary] whitespace-pre-wrap break-words">
                      {m.content || (m.media_url ? "(media)" : "(empty)")}
                    </p>
                    <div className={`text-[9px] mt-1 ${isOut ? "text-[--text-muted] text-right" : "text-[--text-muted]"}`}>
                      {m.timestamp ? new Date(m.timestamp).toLocaleString() : ""}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      <footer className="max-w-3xl mx-auto px-5 py-6 text-center text-[10px] text-[--text-muted]">
        Shared from the Exotic India CRM
      </footer>
    </div>
  );
}
